// ============================================================
// Approval Gates API client
// Docs: https://docs.smplify.com/technical/approval-gates-api
// ============================================================

import { apiGet, apiPost, unwrap } from "./apiClient";
import type { ResponseDTO } from "@/types/api";

export type ApprovalRiskTier =
  | "AUTO"
  | "NOTIFY"
  | "APPROVE"
  | "TWO_PERSON"
  | "DENY";

export type ApprovalStatus =
  | "PENDING"
  | "APPROVED"
  | "DENIED"
  | "EXECUTED"
  | "EXPIRED"
  | "BROKEN_GLASS";

/** Shape returned by /api/v1/approvals/* endpoints. Optional fields are
 * tolerated because the backend may extend the payload over time. */
export interface ApprovalRequest {
  requestId: string;
  tenantId: string;
  privilegeName: string;
  actionType: string;
  riskTier: ApprovalRiskTier;
  status: ApprovalStatus;
  quorumRequired?: number;
  quorumCount?: number;
  expiresAt?: string;
  createdAt?: string;
  requesterId?: string;
  requesterEmail?: string;
  targets?: Array<string | Record<string, unknown>>;
  reason?: string;
  [key: string]: unknown;
}

export type GateMode = "DEFAULT" | "STRICT" | "DISABLED";

export interface ApprovalGateModeResponse {
  tenantId: string;
  mode: GateMode;
  complianceLocked?: boolean;
}

const BASE = "/api/v1/approvals";

export async function getInbox(): Promise<ApprovalRequest[]> {
  const res = await apiGet<ResponseDTO<ApprovalRequest[]>>(`${BASE}/inbox`);
  const data = unwrap(res);
  return Array.isArray(data) ? data : [];
}

export async function getOutbox(): Promise<ApprovalRequest[]> {
  const res = await apiGet<ResponseDTO<ApprovalRequest[]>>(`${BASE}/outbox`);
  const data = unwrap(res);
  return Array.isArray(data) ? data : [];
}

export async function getRequest(id: string): Promise<ApprovalRequest> {
  const res = await apiGet<ResponseDTO<ApprovalRequest>>(`${BASE}/${id}`);
  return unwrap(res);
}

export async function decide(
  id: string,
  decision: "APPROVE" | "DENY",
  reason?: string
): Promise<ApprovalRequest> {
  const res = await apiPost<ResponseDTO<ApprovalRequest>>(
    `${BASE}/${id}/decision`,
    { decision, ...(reason ? { reason } : {}) }
  );
  return unwrap(res);
}

export async function breakGlass(
  id: string,
  reason: string
): Promise<ApprovalRequest> {
  const res = await apiPost<ResponseDTO<ApprovalRequest>>(
    `${BASE}/${id}/break-glass`,
    { reason }
  );
  return unwrap(res);
}

export async function getGateMode(
  tenantId: string
): Promise<ApprovalGateModeResponse> {
  const res = await apiGet<ResponseDTO<ApprovalGateModeResponse>>(
    `/api/v1/tenants/${tenantId}/approval-gate-mode`
  );
  return unwrap(res);
}

// --- Display helpers ----------------------------------------------------

const ACTION_VERBS: Record<string, string> = {
  DEVICE_RETIRE: "Retire device",
  DEVICE_LOCK: "Lock device",
  DEVICE_UNLOCK: "Unlock device",
  DEVICE_UNENROLL: "Unenroll device",
  DEVICE_WIPE: "Erase device",
  POLICY_DEPLOY: "Deploy policy",
  POLICY_DELETE: "Delete policy",
  APP_DEPLOY: "Deploy application",
  APP_DELETE: "Delete application",
  TAG_DELETE: "Delete tag",
  USER_DELETE: "Delete user",
  ROLE_DELETE: "Delete role",
};

export function humanizeAction(actionType: string): string {
  if (!actionType) return "Action";
  if (ACTION_VERBS[actionType]) return ACTION_VERBS[actionType];
  return actionType
    .toLowerCase()
    .split("_")
    .map((w, i) =>
      i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w
    )
    .join(" ");
}

export function tierLabel(tier: ApprovalRiskTier): string {
  switch (tier) {
    case "TWO_PERSON":
      return "Two-person";
    case "APPROVE":
      return "Approve";
    case "NOTIFY":
      return "Notify";
    case "AUTO":
      return "Auto";
    case "DENY":
      return "Denied";
  }
}
